import { useCallback } from 'react';
import AttachmentIcon from '@atlas/uicorn/lib/Icon/AttachmentIcon';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import type { TUploadedAttachment } from '@atlas/tool/box/file/types';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import uploadFiles from '@atlas/tool/box/file/uploadFiles';
import hotkey from '@atlas/tool/box/event/hotkey';
import type { TExample } from '../../preview/modules';

export default function AttachmentsPickerButton(props: TAttachmentsPickerButtonProps) {
    const { disabled, onSelect } = props;

    const onSelectRef = useLastRef(onSelect);
    const handleClick = useCallback(
        () => uploadFiles({ maxFiles: 5 }).then((files) => files && onSelectRef.current?.(files)),
        [onSelectRef],
    );

    return (
        <IconButton
            tooltip={renderTooltip}
            disabled={disabled}
            icon={<AttachmentIcon />}
            tabIndex={props.tabIndex}
            onClick={handleClick}
        />
    );
}

// #region Helpers

export const attachFilesHotkey = hotkey({ mac: 'shift+cmd+a', win: 'ctrl+shift+a' }, 'Composer', 'Attach files', 75);

function renderTooltip() {
    return (
        <>
            Attach files <kbd>{attachFilesHotkey.toString()}</kbd>
        </>
    );
}

// #endregion

// #region Types

export type TAttachmentPickerButtonSelectedItem = TUploadedAttachment;

export type TAttachmentsPickerButtonProps = {
    disabled?: boolean;
    tabIndex?: number;
    onSelect?: (files: TAttachmentPickerButtonSelectedItem[]) => void;
};

// #endregion

export const Example: TExample = {
    render(props) {
        return <AttachmentsPickerButton {...props} />;
    },
    handlers: {
        onSelect: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('AttachmentPickerButton', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
